import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { addDays, format, isPast, startOfWeek, subDays } from "date-fns";
import React, { Fragment, useState } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import LeftArrowIcon from "@/assets/svgs/left-arrow-icon";
import RightArrowIcon from "@/assets/svgs/right-arrow-icon";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { api } from "@/elements/components/utils/convex-api";
import StudentLessonHistoryHome from "../lesson-history/student-lesson-history-home";
import StudentSessionCard from "../shared/student-session-card";

const getFirstDayOfWeek = (date: Date) => {
  const firstDay = startOfWeek(date, { weekStartsOn: 1 });
  return firstDay;
};

function StudentHome() {
  const initialWeekStartDate = getFirstDayOfWeek(new Date());
  const [weekStartDate, setWeekStartDate] = useState(initialWeekStartDate);
  const currentMonth = format(weekStartDate, "MMMM");
  const weekEndDate = addDays(weekStartDate, 6);
  const { data: studentProfile } = useQuery(
    convexQuery(api.student.home.queries.getProfile, {}),
  );
  const studentId = studentProfile?._id;
  const name = studentProfile?.name;
  const { data: hasBookedSession = false } = useQuery(
    convexQuery(api.student.home.queries.hasBookedSession, {}),
  );
  // Convert local dates to UTC dates with same calendar date
  const weekStartDateUTC = new Date(
    Date.UTC(
      weekStartDate.getFullYear(),
      weekStartDate.getMonth(),
      weekStartDate.getDate(),
    ),
  );
  const weekEndDateUTC = new Date(
    Date.UTC(
      weekEndDate.getFullYear(),
      weekEndDate.getMonth(),
      weekEndDate.getDate(),
      23,
      59,
      59,
      999,
    ),
  );
  const { data: weeklySessions = [] } = useQuery({
    ...convexQuery(api.student.home.queries.weeklySessions, {
      startTime: weekStartDateUTC.getTime(),
      endTime: weekEndDateUTC.getTime(),
    }),
    enabled: !!studentId,
  });
  const weeklySessionsCount = weeklySessions.length;

  return (
    <ScrollView showsVerticalScrollIndicator={false}>
      <ResponsiveContent>
        <View className="h-3" />
        <View className="mx-4">
          <View className="gap-1">
            <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[32px] text-gray-800">
              Hello {name}
            </Text>
            <Text className="font-geologica-regular font-normal text-[20px] text-gray-800 leading-[1.5]">
              Welcome to your lesson plan
            </Text>
          </View>
          <View className="h-8" />

          {hasBookedSession ? (
            <Fragment>
              <View className="flex-row items-start justify-between">
                <View className="gap-2">
                  <Text className="font-geologica-medium font-medium text-[20px] text-gray-800 leading-[1.5]">
                    Your weekly schedule
                  </Text>
                  <Text className="font-geologica-regular font-normal text-[16px] text-gray-800 leading-[1.5]">
                    {format(weekStartDate, "do")} - {format(weekEndDate, "do")}{" "}
                    {currentMonth}
                  </Text>
                </View>
                <View className="gap-2 items-center">
                  <View className="flex-row items-center gap-6 self-end">
                    <Pressable
                      className="disabled:opacity-50"
                      disabled={isPast(weekStartDate)}
                      onPress={() =>
                        setWeekStartDate(subDays(weekStartDate, 7))
                      }
                    >
                      <LeftArrowIcon
                        opacity={isPast(weekStartDate) ? 0.5 : 1}
                      />
                    </Pressable>
                    <RightArrowIcon
                      onPress={() =>
                        setWeekStartDate(addDays(weekStartDate, 7))
                      }
                    />
                  </View>

                  <View className="px-2 py-1 bg-teachLoop-creamDark rounded-md">
                    <Text className="font-geologica-medium font-medium text-xs text-teachLoop-textPrimary leading-[1.5] uppercase">
                      {weeklySessionsCount} lessons left
                    </Text>
                  </View>
                </View>
              </View>
              <View className="h-6" />
              <View className="gap-4">
                {weeklySessions.length > 0 ? (
                  weeklySessions
                    .sort((a, b) => a.startTime - b.startTime)
                    .map((session) => {
                      return (
                        <StudentSessionCard
                          key={session._id}
                          session={session}
                        />
                      );
                    })
                ) : (
                  <Text className="font-geologica-regular text-base text-gray-800 leading-[1.5]">
                    You have no more lessons this week
                  </Text>
                )}
              </View>
              <View className="h-6" />
            </Fragment>
          ) : (
            <Fragment>
              <View className="px-4 py-10 bg-teachLoop-creamDark rounded-2xl">
                <Text className="font-geologica-regular text-center text-base text-gray-800 leading-[1.5]">
                  You don&apos;t have any lessons booked yet.
                </Text>
              </View>
              <View className="h-8" />
            </Fragment>
          )}
          <StudentLessonHistoryHome />
        </View>
      </ResponsiveContent>
    </ScrollView>
  );
}

export default StudentHome;
